import { lazy, Suspense, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ChatProvider } from "../context/ChatContext";

import ErrorBoundary from "../components/ErrorBoundary";
import Sidebar from "../components/Sidebar";
import useChatbotConfig from "../hooks/useChatbotConfig";
import Intents from "../pages/Advanced/Intents";
import Variables from "../pages/Advanced/Variables";
import { useConnectionStatus } from "../hooks/useConnectionStatus";
import BotStates from "../pages/admin/BotStates";
import UserConnections from "../pages/admin/UserConnections";
import ChatsEvaluation from "../pages/AnalyticsChatsEvaluation";
import WhatsAppTemplatesAdmin from "../pages/admin/WhatsappTemplateAdmin";
import SystemAlerts from "../pages/admin/SystemAlerts";
import News from "../pages/admin/News";
import { HOME_TOUR } from "../constants/tours.const.jsx";
import TourProvider from "../components/info/TourProvider";

const ReactivationDetails = lazy(() => import("../pages/reactivation/ReactivationDetails"));
const ReactivationHistory = lazy(() => import("../pages/reactivation/ReactivationHistory"));

const Home = lazy(() => import("../pages/Home"));
const HistoryChanges = lazy(() => import("../pages/HistoryChanges"));
const Updates = lazy(() => import("../pages/extras/Updates"));
const BotState = lazy(() => import("../pages/BotState"));

const Chats = lazy(() => import("../pages/Chats"));

const HistoryTriniPlusUsage = lazy(() => import("../pages/HistoryTriniPlusUsage"));

const Contacts = lazy(() => import("../pages/Contacts"));
const ContactsGroups = lazy(() => import("../pages/ContactsGroups"));
const ContactsAreas = lazy(() => import("../pages/ContactsAreas"));
const ContactsRegister = lazy(() => import("../pages/ContactsRegister"));

const Campaign = lazy(() => import("../pages/campaigns/CampaignCreator"));
const CampaignHistory = lazy(() => import("../pages/campaigns/CampaignHistory"));
const CampaignSelected = lazy(() => import("../pages/campaigns/CampaignSelected"));
const CampaignGeneralData = lazy(() => import("../pages/campaigns/CampaignGeneralData"));
const CampaignSenderDetails = lazy(() => import("../pages/campaigns/CampaignSenderDetails"));
const TemplatesAdmin = lazy(() => import("../pages/TemplatesAdministration"));
const CreateTemplate = lazy(() => import("../pages/CreateTemplate"));

const Responses = lazy(() => import("../pages/Responses"));
const ResponsesSecondary = lazy(() => import("../pages/ResponsesSecondary"));
const ResponsesQuick = lazy(() => import("../pages/ResponsesQuick"));

const Forms = lazy(() => import("../pages/Forms"));
const AddSurveyForm = lazy(() => import("../pages/forms/AddSurveyForm"));
const AddBranchForm = lazy(() => import("../pages/forms/AddBranchForm"));
const NewFormSchema = lazy(() => import("../pages/forms/NewFormSchema"));
const Schemas = lazy(() => import("../pages/forms/Schemas"));
const FormSchemaDiagram = lazy(() => import("../pages/forms/FormSchemaDiagram"));
const FormSchemaSettings = lazy(() => import("../pages/forms/FormSchemaSettings"));
const CreateAdForm = lazy(() => import("../pages/forms/CreateAdForm"));

const ContactForm = lazy(() => import("../pages/forms/ContactForm"));

const ProductForm = lazy(() => import("../pages/forms/ProductForm"));
const ProductFormInventory = lazy(() =>
  import("../pages/forms/ProductFormInventory")
);
const ProductReorder = lazy(() => import("../pages/forms/ProductReorder"));
const ProductExamples = lazy(() => import("../pages/forms/ProductExamples"));
const ProductSynonyms = lazy(() => import("../pages/forms/ProductSynonyms"));
const ProductSettings = lazy(() => import("../pages/forms/ProductSettings"));

const BranchesForm = lazy(() => import("../pages/forms/BranchesForm"));
const BranchesQuestionsForm = lazy(() =>
  import("../pages/forms/BranchesQuestionsForm")
);
const BranchesExamples = lazy(() => import("../pages/forms/BranchesExamples"));
const BranchesFormSettings = lazy(() =>
  import("../pages/forms/BranchesFormSettings")
);

const SurveyForm = lazy(() => import("../pages/forms/SurveyForm"));
const SurveyFormURLs = lazy(() => import("../pages/forms/SurveyFormURLs"));
const SurveyFormSendSurvey = lazy(() =>
  import("../pages/forms/SurveyFormSendSurvey")
);
const SurveyFormSettings = lazy(() =>
  import("../pages/forms/SurveyFormSettings")
);

const TestBot = lazy(() => import("../pages/TestBot"));

const Analytics = lazy(() => import("../pages/Analytics"));
const AnalyticsMessages = lazy(() => import("../pages/AnalyticsMessages"));
const AnalyticsMessagesTemplates = lazy(() => import("../pages/AnalyticsMessagesTemplates"));
const AnalyticsIntents = lazy(() => import("../pages/AnalyticsIntents"));
const AnalyticsForms = lazy(() => import("../pages/AnalyticsForms"));

const Billings = lazy(() => import("../pages/Billings"));

const Plugins = lazy(() => import("../pages/plugins/Plugins"));

const Examples = lazy(() => import("../pages/Advanced/Examples"));
const Entities = lazy(() => import("../pages/Advanced/Entities"));

const Assistants = lazy(() => import("../pages/assistants/Assistants"));
const MediaStorage = lazy(() => import("../pages/storage/MediaStorage"));
const AddAssistant = lazy(() => import("../pages/assistants/AddAssistant"));
const AssistantSettings = lazy(() =>
  import("../pages/assistants/AssistantSettings")
);

const SettingsGeneral = lazy(() => import("../pages/SettingsGeneral"));
const SettingsAttention = lazy(() => import("../pages/SettingsAttention"));
const SettingsWebchat = lazy(() => import("../pages/SettingsWebchat"));
const SettingsNotification = lazy(() =>
  import("../pages/SettingsNotification")
);
const SettingsPrivacy = lazy(() => import("../pages/SettingsPrivacy"));
const SettingsAdminUsers = lazy(() => import("../pages/SettingsAdminUsers"));

const UserSettings = lazy(() => import("../pages/user/UserSettings"));
const Error = lazy(() => import("../pages/Error"));
const ApiDocumentation = lazy(() =>
  import("../pages/documentation/ApiDocumentation")
);

const SettingsQuickBudget = lazy(() => import("../pages/SettingsQuickBudget"));

const AppRouter = () => {
  const navigate = useNavigate();
  useConnectionStatus();
  const { user, bots, setBotSelected, botPermissions, hasBots, botPlan } =
    useChatbotConfig();
  const initDarkMode = () => {
    if (
      localStorage.getItem("mode") &&
      localStorage.getItem("mode") === "dark"
    ) {
      document.body.classList.add("dark");
      return true;
    } else {
      document.body.classList.add("light");
      return false;
    }
  };
  const [toggleDarkMode, setToggleDarkMode] = useState(initDarkMode);

  const handletoggleDarkMode = () => {
    if (!toggleDarkMode) {
      document.body.classList.remove("light");
      document.body.classList.add("dark");
      localStorage.setItem("mode", "dark");
    } else {
      document.body.classList.remove("dark");
      document.body.classList.add("light");
      localStorage.setItem("mode", "light");
    }
    setToggleDarkMode(!toggleDarkMode);
  };
  const [toggle, setToggle] = useState(true);
  const handletoggle = () => {
    setToggle(!toggle);
  };
  const [toggleResponsive, setToggleResponsive] = useState(false);
  const handletoggleResponsive = () => {
    setToggleResponsive(!toggleResponsive);
  };

  const changeBot = (botid) => {
    const b = bots.find((bot) => bot.id === botid);
    setBotSelected(b);
    localStorage.setItem("bot", b.id);
    navigate("/home");
  };

  return (
    <section>
      <Sidebar
        toggleDarkMode={toggleDarkMode}
        handletoggleDarkMode={handletoggleDarkMode}
        toggle={toggle}
        handletoggle={handletoggle}
        toggleResponsive={toggleResponsive}
        setToggleResponsive={setToggleResponsive}
        handletoggleResponsive={handletoggleResponsive}
        changeBot={changeBot}
      />
      <ErrorBoundary>
        <Suspense fallback={null}>
          <TourProvider steps={[]}>
            <Routes>
              <Route path="/home" element={<Home />} />
              {user?.admin && (
                <>
                  <Route path="/admin/users" element={<UserConnections />} />
                  <Route path="/admin/bots" element={<BotStates />} />
                  <Route path="/admin/templates" element={<WhatsAppTemplatesAdmin />} />
                  <Route path="/admin/system-alerts" element={<SystemAlerts />} />
                  <Route path="/admin/news" element={<News />} />
                </>
              )}

              {user?.admin && (
                <>
                  <Route path="/massive/reactivation/details" element={<ReactivationDetails />} />
                  <Route path="/massive/reactivation/history" element={<ReactivationHistory />} />
                </>
              )}

              {hasBots && (
                <>
                  <Route path="/historychanges" element={<HistoryChanges />} />

                  {(botPlan?.ai_model && botPermissions?.responses) && (
                    <>
                      <Route path="/responses" element={<Responses />} />
                      <Route
                        path="/responses/secondary"
                        element={<ResponsesSecondary />}
                      />
                    </>
                  )}

                  {(!botPlan?.ai_model && botPermissions?.responses) && (
                    <Route
                      path="/responses/quick"
                      element={<ResponsesQuick />}
                    />
                  )}

                  <Route path="/testbot" element={<TestBot />} />

                  {(botPermissions?.forms || !botPlan?.ai_model) && (
                    <Route path="/forms/contact" element={<ContactForm />} />
                  )}

                  {botPermissions?.forms && (
                    <>
                      <Route path="/forms" element={<Forms />} />
                      <Route
                        path="/forms/add/survey"
                        element={<AddSurveyForm />}
                      />
                      <Route
                        path="/forms/add/branch"
                        element={<AddBranchForm />}
                      />
                      <Route
                        path="/forms/add/schema"
                        element={<NewFormSchema />}
                      />
                      <Route path="/forms/schemas" element={<Schemas />} />
                      <Route
                        path="/forms/create/ad"
                        element={<CreateAdForm />}
                      />
                      {botPermissions?.responses && (
                        <>
                          <Route
                            path="/forms/products/:idForm"
                            element={<ProductForm />}
                          />
                          <Route
                            path="/forms/products/reorder/:idForm"
                            element={<ProductReorder />}
                          />
                          <Route
                            path="/forms/products/inventory/:idForm"
                            element={<ProductFormInventory />}
                          />
                          <Route
                            path="/forms/products/examples/:idForm"
                            element={<ProductExamples />}
                          />
                          <Route
                            path="/forms/products/synonyms/:idForm"
                            element={<ProductSynonyms />}
                          />
                          <Route
                            path="/forms/products/settings/:idForm"
                            element={<ProductSettings />}
                          />
                          <Route
                            path="/forms/branches/:idForm"
                            element={<BranchesForm />}
                          />
                          <Route
                            path="/forms/branches/questions/:idForm"
                            element={<BranchesQuestionsForm />}
                          />
                          <Route
                            path="/forms/branches/examples/:idForm"
                            element={<BranchesExamples />}
                          />
                          <Route
                            path="/forms/branches/settings/:idForm"
                            element={<BranchesFormSettings />}
                          />
                          <Route
                            path="/forms/schema/:idForm"
                            element={<FormSchemaDiagram />}
                          />
                          <Route
                            path="/forms/schema/settings/:idForm"
                            element={<FormSchemaSettings />}
                          />
                        </>
                      )}
                      <Route
                        path="/forms/survey/:idForm"
                        element={<SurveyForm />}
                      />
                      <Route
                        path="/forms/survey/links/:idForm"
                        element={<SurveyFormURLs />}
                      />
                      <Route
                        path="/forms/survey/send/:idForm"
                        element={<SurveyFormSendSurvey />}
                      />
                      <Route
                        path="/forms/survey/settings/:idForm"
                        element={<SurveyFormSettings />}
                      />
                    </>
                  )}

                  {botPermissions?.plugins && (
                    <Route path="/plugins" element={<Plugins />} />
                  )}

                  <Route path="/analytics" element={<Analytics />} />
                  <Route
                    path="/analytics/messages"
                    element={<AnalyticsMessages />}
                  />
                  <Route
                    path="/analytics/messages/templates"
                    element={<AnalyticsMessagesTemplates />}
                  />
                  {botPlan?.ai_model && (
                    <>
                      <Route path="/analytics/chats-evaluation" element={<ChatsEvaluation />} />
                      <Route
                        path="/analytics/intents"
                        element={<AnalyticsIntents />}
                      />
                      <Route
                        path="/analytics/forms"
                        element={<AnalyticsForms />}
                      />
                    </>
                  )}

                  <Route path="/state" element={<BotState />} />
                  {botPermissions?.chats && (
                    <Route
                      path="/chats"
                      element={
                        <ChatProvider>
                          <Chats />
                        </ChatProvider>
                      }
                    />
                  )}
                  {botPermissions?.contacts && (
                    <>
                      <Route path="/contacts" element={<Contacts />} />
                      <Route
                        path="/contacts/groups"
                        element={<ContactsGroups />}
                      />
                      <Route
                        path="/contacts/areas"
                        element={<ContactsAreas />}
                      />
                      <Route
                        path="/contacts/register"
                        element={<ContactsRegister />}
                      />
                    </>
                  )}
                  {botPermissions?.advanced_configuration && (
                    <>
                      {!botPlan?.ai_model ? (
                        <Route path="/advanced/entities" element={<Entities />} />
                      ) : (
                        <>
                          <Route path="/advanced/examples" element={<Examples />} />
                          <Route path="/advanced/entities" element={<Entities />} />
                          <Route path="/advanced/intents" element={<Intents />} />
                          <Route path="/advanced/variables" element={<Variables />} />
                        </>
                      )}
                    </>
                  )}
                  {botPermissions?.plugins && (
                    <>
                      <Route path="/assistants" element={<Assistants />} />
                      <Route
                        path="/assistants/add"
                        element={<AddAssistant />}
                      />
                      <Route
                        path="/assistants/settings/:idAssistant"
                        element={<AssistantSettings />}
                      />
                    </>
                  )}

                  <Route path="/storage" element={<MediaStorage />} />

                  {botPermissions?.configuration && (
                    <>
                      <Route
                        path="/settings/general"
                        element={<SettingsGeneral />}
                      />
                      <Route
                        path="/settings/attention"
                        element={<SettingsAttention />}
                      />
                      <Route
                        path="/settings/notifications"
                        element={<SettingsNotification />}
                      />
                      <Route
                        path="/settings/access"
                        element={<SettingsPrivacy />}
                      />
                      <Route
                        path="/settings/admin"
                        element={<SettingsAdminUsers />}
                      />
                      <Route
                        path="/settings/webchat"
                        element={<SettingsWebchat />}
                      />
                      <Route
                        path="/settings/budget"
                        element={<SettingsQuickBudget />}
                      />
                    </>
                  )}
                  {botPermissions?.pricing && (
                    <Route path="/billings" element={<Billings />} />
                  )}

                  {botPermissions?.pricing && (
                    <Route path="/billings/triniplus/history" element={<HistoryTriniPlusUsage />} />
                  )}

                  {botPermissions?.campaigns && (
                    <>
                      <Route path="/campaign" element={<Campaign />} />
                      <Route
                        path="/campaign/history"
                        element={<CampaignHistory />}
                      />
                      <Route
                        path="/campaign/:idBroadcast"
                        element={<CampaignSelected />}
                      />
                      <Route
                        path="/campaign/templates"
                        element={<TemplatesAdmin />}
                      />
                      <Route
                        path="/create/templates"
                        element={<CreateTemplate />}
                      />
                      <Route
                        path="/campaign/general-data/:idBroadcast"
                        element={<CampaignGeneralData />}
                      />
                      <Route
                        path="/campaign/sender-details/:idBroadcast"
                        element={<CampaignSenderDetails />}
                      />
                    </>
                  )}
                  <Route path="/documentation" element={<ApiDocumentation />} />
                  <Route end path="/updates" element={<Updates />} />
                </>
              )}
              <Route path="/settings/user" element={<UserSettings />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
              <Route path="/error" element={<Error />} />
            </Routes>
          </TourProvider>
        </Suspense>
      </ErrorBoundary>
    </section>
  );
};

export default AppRouter;
